import {
  Button,
  Group,
  Input,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@assetwatch/ui";
import type { TFunction } from "i18next";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MotionAmplificationJob } from "../../clients/motion-amplification";
import { useUpdateMotionAmplificationJobMutation } from "../../mutations/useUpdateMotionAmplificationJobMutation";
import {
  useMotionAmplificationAssignmentCustomersQuery,
  useMotionAmplificationAssignmentFacilitiesQuery,
  useMotionAmplificationAssignmentMachinesQuery,
} from "../../queries/useMotionAmplificationAssignmentQuery";

function valueOrDash(value: unknown, fallback = "—") {
  return value === undefined || value === null ? fallback : String(value);
}

function toSelectOptions(options: { id: string; name: string }[] | undefined) {
  return (options ?? []).map((option) => ({
    value: option.id,
    label: option.name,
  }));
}

function parameterRows(job: MotionAmplificationJob, t: TFunction) {
  const p = job.parameters ?? {};
  const rows = [
    [
      t("motionAmplification.name"),
      job.name ||
        job.fileName ||
        t("motionAmplification.jobFallback", { id: job.jobId }),
      t("motionAmplification.displayNameDescription"),
    ],
    ...(job.description
      ? [
          [
            t("motionAmplification.description"),
            job.description,
            t("motionAmplification.optionalContextDescription"),
          ],
        ]
      : []),
    [
      t("motionAmplification.status"),
      job.status,
      t("motionAmplification.currentStatusDescription"),
    ],
    [
      t("motionAmplification.customer"),
      job.assignment?.customerName ?? t("motionAmplification.none"),
      t("motionAmplification.customerDescription"),
    ],
    [
      t("motionAmplification.facility"),
      job.assignment?.facilityName ?? t("motionAmplification.none"),
      t("motionAmplification.facilityDescription"),
    ],
    [
      t("motionAmplification.machine"),
      job.assignment?.machineName ?? t("motionAmplification.none"),
      t("motionAmplification.machineDescription"),
    ],
    ...(job.status === "Failed"
      ? [
          [
            t("motionAmplification.failurePhase"),
            valueOrDash(job.phase),
            t("motionAmplification.failurePhaseDescription"),
          ],
          [
            t("motionAmplification.failureReason"),
            valueOrDash(job.errorMessage),
            t("motionAmplification.failureReasonDescription"),
          ],
        ]
      : []),
    [
      t("motionAmplification.preset"),
      valueOrDash(job.preset),
      t("motionAmplification.descriptions.preset"),
    ],
    [
      t("motionAmplification.frameRate"),
      valueOrDash(p.fps),
      t("motionAmplification.frameRateDescription"),
    ],
    [
      t("motionAmplification.frequencyStart"),
      valueOrDash(p.frequencyLowHz),
      t("motionAmplification.descriptions.frequencyStart"),
    ],
    [
      t("motionAmplification.frequencyEnd"),
      valueOrDash(p.frequencyHighHz),
      t("motionAmplification.descriptions.frequencyEnd"),
    ],
    [
      t("motionAmplification.alpha"),
      valueOrDash(p.alpha),
      t("motionAmplification.descriptions.alpha"),
    ],
    [
      t("motionAmplification.lambdaC"),
      valueOrDash(p.lambdaC),
      t("motionAmplification.descriptions.lambdaC"),
    ],
    [
      t("motionAmplification.stabilization"),
      p.stabilize
        ? t("motionAmplification.enabled")
        : t("motionAmplification.disabled"),
      t("motionAmplification.descriptions.stabilization"),
    ],
    [
      t("motionAmplification.stabilizationSmoothing"),
      valueOrDash(p.stabilizeSmooth),
      t("motionAmplification.descriptions.stabilizationSmoothing"),
    ],
    [
      t("motionAmplification.maximumWidth"),
      valueOrDash(p.maxWidth, t("motionAmplification.originalWidth")),
      t("motionAmplification.descriptions.maximumWidth"),
    ],
    [
      t("motionAmplification.pyramidLevels"),
      valueOrDash(p.pyramidLevels),
      t("motionAmplification.descriptions.pyramidLevels"),
    ],
    [
      t("motionAmplification.chromaAttenuation"),
      valueOrDash(p.chromAttenuation),
      t("motionAmplification.descriptions.chromaAttenuation"),
    ],
    [
      t("motionAmplification.chunkSize"),
      valueOrDash(p.chunkSize),
      t("motionAmplification.descriptions.chunkSize"),
    ],
    [
      t("motionAmplification.chunkOverlap"),
      valueOrDash(p.overlap, t("motionAmplification.automatic")),
      t("motionAmplification.descriptions.chunkOverlap"),
    ],
    [
      t("motionAmplification.stabilizationPasses"),
      valueOrDash(p.stabilizeSmoothPasses),
      t("motionAmplification.descriptions.stabilizationPasses"),
    ],
    [
      t("motionAmplification.maximumFeatures"),
      valueOrDash(p.stabilizeMaxFeatures),
      t("motionAmplification.descriptions.maximumFeatures"),
    ],
    [
      t("motionAmplification.ransacThreshold"),
      valueOrDash(p.stabilizeRansacPx),
      t("motionAmplification.descriptions.ransacThreshold"),
    ],
    [
      t("motionAmplification.fastThreshold"),
      valueOrDash(p.stabilizeFastThreshold),
      t("motionAmplification.descriptions.fastThreshold"),
    ],
    [
      t("motionAmplification.cropRegion"),
      p.crop ? JSON.stringify(p.crop) : t("motionAmplification.none"),
      t("motionAmplification.descriptions.cropRegion"),
    ],
    [
      t("motionAmplification.heatmapOutput"),
      p.heatmap
        ? t("motionAmplification.enabled")
        : t("motionAmplification.disabled"),
      t("motionAmplification.descriptions.heatmapOutput"),
    ],
    [
      t("motionAmplification.sideBySideOutput"),
      p.sideBySide
        ? t("motionAmplification.enabled")
        : t("motionAmplification.disabled"),
      t("motionAmplification.descriptions.sideBySideOutput"),
    ],
    [
      t("motionAmplification.heatmapAlpha"),
      valueOrDash(p.heatmapAlpha),
      t("motionAmplification.descriptions.heatmapOpacity"),
    ],
  ];
  return rows;
}

function MotionAmplificationDetailsContent({
  job,
  open,
  onOpenChange,
}: {
  job: MotionAmplificationJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const updateJob = useUpdateMotionAmplificationJobMutation();
  const fallbackName = t("motionAmplification.jobFallback", { id: job.jobId });
  const [initialName, setInitialName] = useState(
    job.name ?? job.fileName ?? fallbackName,
  );
  const [initialMachineId, setInitialMachineId] = useState(
    job.assignment?.machineId ?? null,
  );
  const [name, setName] = useState(initialName);
  const [customerId, setCustomerId] = useState<string | null>(
    job.assignment?.customerId ?? null,
  );
  const [facilityId, setFacilityId] = useState<string | null>(
    job.assignment?.facilityId ?? null,
  );
  const [machineId, setMachineId] = useState<string | null>(initialMachineId);
  const [error, setError] = useState<string | null>(null);
  const customersQuery = useMotionAmplificationAssignmentCustomersQuery();
  const facilitiesQuery =
    useMotionAmplificationAssignmentFacilitiesQuery(customerId);
  const machinesQuery =
    useMotionAmplificationAssignmentMachinesQuery(facilityId);
  const hasPartialAssignment =
    machineId === null && (customerId !== null || facilityId !== null);
  const hasChanges =
    name.trim() !== initialName.trim() || machineId !== initialMachineId;

  async function save() {
    if (!hasChanges || hasPartialAssignment || !name.trim()) return;
    setError(null);
    try {
      const updatedJob = await updateJob.mutateAsync({
        jobId: job.jobId,
        name: name.trim(),
        machineId,
      });
      const updatedName =
        updatedJob.name ?? updatedJob.fileName ?? fallbackName;
      const updatedMachineId = updatedJob.assignment?.machineId ?? null;
      setName(updatedName);
      setCustomerId(updatedJob.assignment?.customerId ?? null);
      setFacilityId(updatedJob.assignment?.facilityId ?? null);
      setMachineId(updatedMachineId);
      setInitialName(updatedName);
      setInitialMachineId(updatedMachineId);
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : t("motionAmplification.saveVideoError"),
      );
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!updateJob.isPending) onOpenChange(nextOpen);
      }}
      title={`${t("motionAmplification.videoDetails")} • ${name || job.fileName || t("motionAmplification.selectVideo")}`}
      size="lg"
      footer={
        job.status === "Completed" ? (
          <Group justify="end">
            <Button
              onClick={() => void save()}
              disabled={
                !hasChanges ||
                !name.trim() ||
                hasPartialAssignment ||
                updateJob.isPending
              }
              loading={updateJob.isPending}
              loadingText={t("motionAmplification.saving")}
            >
              {t("motionAmplification.save")}
            </Button>
          </Group>
        ) : undefined
      }
    >
      <Tabs defaultValue="parameters">
        <div className="min-w-0 overflow-x-auto">
          <TabsList>
            <TabsTrigger value="parameters">
              {t("motionAmplification.parameters")}
            </TabsTrigger>
            <TabsTrigger value="camera">
              {t("motionAmplification.cameraSettings")}
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="parameters">
          <div className="mt-(--spacing-16)">
            {error && <Text tone="negative">{error}</Text>}
            <div className="[&_thead_th]:bg-surface-container [&_tbody_tr_td]:py-(--spacing-4)">
              <Table size="sm">
                <TableHeader>
                  <TableRow section="header">
                    <TableHead scope="col">
                      {t("motionAmplification.parameter")}
                    </TableHead>
                    <TableHead scope="col">
                      {t("motionAmplification.value")}
                    </TableHead>
                    <TableHead scope="col">
                      {t("motionAmplification.parameterDescription")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parameterRows(job, t).map(([parameter, value, limits]) => {
                    const isEditable = job.status === "Completed";
                    let editableValue: ReactNode = value;

                    if (
                      isEditable &&
                      parameter === t("motionAmplification.name")
                    ) {
                      editableValue = (
                        <Input
                          aria-label={parameter}
                          size="sm"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          maxLength={100}
                          required
                          disabled={updateJob.isPending}
                          reserveSubtextSpace={false}
                        />
                      );
                    } else if (
                      isEditable &&
                      parameter === t("motionAmplification.customer")
                    ) {
                      editableValue = (
                        <Select
                          placeholder={t("motionAmplification.selectCustomer")}
                          searchable
                          size="sm"
                          options={toSelectOptions(customersQuery.data)}
                          value={customerId}
                          onChange={(nextCustomerId) => {
                            setCustomerId(nextCustomerId);
                            setFacilityId(null);
                            setMachineId(null);
                          }}
                          disabled={
                            customersQuery.isLoading || updateJob.isPending
                          }
                          reserveSubtextSpace={false}
                        />
                      );
                    } else if (
                      isEditable &&
                      parameter === t("motionAmplification.facility")
                    ) {
                      editableValue = (
                        <Select
                          placeholder={t("motionAmplification.selectFacility")}
                          searchable
                          size="sm"
                          options={toSelectOptions(facilitiesQuery.data)}
                          value={facilityId}
                          onChange={(nextFacilityId) => {
                            setFacilityId(nextFacilityId);
                            setMachineId(null);
                          }}
                          disabled={
                            customerId === null ||
                            facilitiesQuery.isLoading ||
                            updateJob.isPending
                          }
                          reserveSubtextSpace={false}
                        />
                      );
                    } else if (
                      isEditable &&
                      parameter === t("motionAmplification.machine")
                    ) {
                      editableValue = (
                        <Select
                          placeholder={t("motionAmplification.selectMachine")}
                          searchable
                          size="sm"
                          options={toSelectOptions(machinesQuery.data)}
                          value={machineId}
                          onChange={setMachineId}
                          disabled={
                            facilityId === null ||
                            machinesQuery.isLoading ||
                            updateJob.isPending
                          }
                          description={t(
                            "motionAmplification.machineAssignmentHint",
                          )}
                          error={
                            hasPartialAssignment
                              ? t(
                                  "motionAmplification.machineAssignmentRequired",
                                )
                              : undefined
                          }
                          reserveSubtextSpace={false}
                        />
                      );
                    }

                    return (
                      <TableRow key={parameter}>
                        <TableCell>{parameter}</TableCell>
                        <TableCell>{editableValue}</TableCell>
                        <TableCell>{limits}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="camera">
          <div className="mt-(--spacing-16)">
            <Text tone="subtle">
              {t("motionAmplification.noCameraSettings")}
            </Text>
          </div>
        </TabsContent>
      </Tabs>
    </Modal>
  );
}

export function MotionAmplificationDetailsModal({
  job,
  open,
  onOpenChange,
}: {
  job: MotionAmplificationJob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  if (!job) {
    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={`${t("motionAmplification.videoDetails")} • ${t("motionAmplification.selectVideo")}`}
        size="lg"
      />
    );
  }
  return (
    <MotionAmplificationDetailsContent
      key={job.jobId}
      job={job}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}
